import type { CSSProperties, FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'

import { HeroSearch } from '@/components/home/hero-search'
import { TrustIndicators } from '@/components/home/trust-indicators'
import { MarketHeroVisual } from '@/components/home/market-hero/market-hero-visual'

/** Staggered entrance for the left column, in the order a reader scans it. */
const rise = (delay: number): CSSProperties => ({ '--delay': `${delay}ms` } as CSSProperties)

const ctaBase = 'mta-focus hero-label group inline-flex h-[54px] items-center justify-center gap-2.5 rounded-mta-sm border-2 border-ink-900 px-5 text-[15px] tracking-[0.02em] shadow-offset-sm transition-transform duration-200 ease-mta hover:-translate-y-1 hover:shadow-offset focus-visible:-translate-y-1 sm:h-[58px] sm:px-6 sm:text-[16px]'

export const Hero: FC = (): ReactNode => {
    return (
        <section id='hero' className='relative isolate overflow-hidden bg-canvas'>
            <div className='mx-auto w-full max-w-[1540px] px-5 pb-10 pt-1 sm:px-8 lg:px-[clamp(24px,4vw,72px)] lg:pb-14'>
                <div className='grid items-center gap-10 lg:grid-cols-[minmax(0,48fr)_minmax(0,52fr)] lg:gap-5'>
                    {/* ---------------- Left: the argument ---------------- */}
                    <div className='order-1'>
                        <h1 className='hero-headline hero-rise text-ink-900' style={rise(0)}>
                            <span className='block'>Buy.</span>
                            <span className='block text-brand-red'>Sell.</span>
                            <span className='block'>Connect.</span>
                        </h1>

                        <div className='hero-rise mt-4' style={rise(90)}>
                            <span aria-hidden='true' className='block h-[5px] w-[104px] bg-brand-red' />

                            {/*
                                Capped and balanced: this line is far longer than
                                the one it replaced, and `hero-subhead` is 900
                                weight at `line-height: 1`, so without a measure
                                it sets as a single crowded block on a wide
                                screen and orphans a word on a narrow one.
                            */}
                            <p
                                className='hero-subhead mt-3 max-w-[16ch] text-balance text-ink-900 sm:max-w-[20ch] lg:max-w-[760px]'
                                style={{ fontSize: 'clamp(1.2rem, 2vw, 2.05rem)' }}
                            >
                                Telegram communities, turned into real marketplaces.
                            </p>
                        </div>

                        <div className='hero-rise mt-3 flex max-w-[620px] flex-col gap-1 lg:max-w-[700px]' style={rise(160)}>
                            <p className='text-[12px] font-normal leading-[1.45] text-ink-900/65 sm:text-[13px]'>
                                Trusted listings from Telegram communities.
                            </p>

                            <p className='text-[12px] font-normal leading-[1.45] text-ink-900/65 sm:text-[13px]'>
                                Accounts, channels, electronics, digital assets and local deals.
                            </p>
                        </div>

                        <div className='hero-rise mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap' style={rise(230)}>
                            <Link href='/browse' className={`${ctaBase} w-full bg-brand-red-ink text-white hover:bg-ink-900 sm:w-auto`}>
                                Shop the drop

                                <ArrowRight
                                    className='size-4 transition-transform duration-200 ease-mta group-hover:translate-x-1.5 sm:size-5'
                                    aria-hidden='true'
                                />
                            </Link>

                            <Link href='/communities/new' className={`${ctaBase} w-full bg-paper text-ink-900 hover:text-brand-red-ink sm:w-auto`}>
                                Start selling

                                <ArrowRight
                                    className='size-4 transition-transform duration-200 ease-mta group-hover:translate-x-1.5 sm:size-5'
                                    aria-hidden='true'
                                />
                            </Link>
                        </div>

                        <TrustIndicators className='hero-rise mt-5' style={rise(300)} />
                    </div>

                    {/* ---------------- Right: the marketplace composition ----------------
                        Pushed slightly out of the gutter on wide screens so the
                        outermost cards and marker strokes bleed past the column. */}
                    <div className='order-2 -mx-2 sm:mx-0 lg:-mr-[clamp(0px,3vw,56px)]'>
                        <MarketHeroVisual />
                    </div>
                </div>

                {/* ---------------- Search: anchors the bottom of the hero ---------------- */}
                <HeroSearch className='hero-rise mt-8 lg:mt-10' style={rise(380)} />
            </div>
        </section>
    )
}
